/**
 * One onboarding or offboarding as a condensed record to print or save as a
 * PDF: who it is about, what is done and by whom, what is still to do, and
 * (for those who may read the log) that the audit log is intact. Something to
 * hand an auditor or an outside partner as proof or as a handoff.
 *
 * It is drawn from the same responses as the board, so it shows exactly what
 * the viewer may see and no more: `cardFor()` has already decided that, and
 * a private value never reaches a list. The browser's print dialog makes the
 * file ("Save as PDF"), so there is nothing to generate or store, and the
 * paper layout is the stylesheet's `@media print`.
 */

import { useEffect } from "react";
import type { AuditResponse, BoardCard, BoardResponse, CardFull, EmployeeSummary } from "@utuwrk/core";
import { useOrg } from "../lib/org.js";
import { boardOf } from "../lib/router.js";
import {
  EMPLOYEE_STATE_LABEL,
  SLA_LABEL,
  STATE_LABEL,
  anchorLabel,
  dayText,
  dueText,
  durationText,
  kindWord,
  listText,
  plural,
  whenText,
  words,
} from "../lib/words.js";
import { Icon } from "./Icon.js";
import { Loading, PageHeader, Problem, useLoad } from "./bits.js";

const isDone = (entry: BoardCard): boolean => ["completed", "skipped"].includes(entry.card.state);

/** What was filled in on a finished card, for the record: private values are never in a list, and are left out here. */
function recorded(card: CardFull): string[] {
  const lines: string[] = [];
  for (const field of card.fields)
    if (!field.pii && !field.hidden && field.value) lines.push(`${field.label}: ${field.value}`);
  return lines;
}

export function Summary({ id }: { id: string }) {
  const { api, org, nameOf } = useOrg();
  const { data, error } = useLoad(async () => {
    const board = await api.board(id);
    // Both are extras: the record stands without a person's page or a log the viewer may not read.
    const [employee, audit] = await Promise.all([
      api
        .employee(board.instance.employeeId)
        .then((response) => response.employee)
        .catch(() => null),
      board.canReadAudit ? api.audit(id).catch(() => null) : Promise.resolve(null),
    ]);
    return { board, employee, audit, at: Date.now() };
  }, [api, id]);

  // The PDF is named after the page title, so give it one that says what it is.
  const person = data?.board.instance.employeeName;
  useEffect(() => {
    if (!person) return;
    const was = document.title;
    document.title = `${person} - handoff record`;
    return () => {
      document.title = was;
    };
  }, [person]);

  if (!data && !error) return <Loading title="Export as PDF" />;
  if (!data) {
    return (
      <section className="page">
        <PageHeader title="Export as PDF" />
        <Problem>{error}</Problem>
      </section>
    );
  }

  const { board, employee, audit, at } = data;
  const { instance } = board;
  const tasks = board.cards.filter((entry) => entry.card.kind === "task");
  const outstanding = tasks.filter((entry) => !isDone(entry));
  const finished = tasks.filter(isDone);
  const blocked = outstanding.filter((entry) => entry.card.state === "blocked").length;
  const word = kindWord(instance);
  const status = instance.cancelled
    ? "Cancelled"
    : instance.closedAt
      ? "Finished"
      : outstanding.length === 0
        ? "Nothing left to do"
        : "In progress";

  return (
    <section className="page page-wide handoff">
      <PageHeader
        back={{ to: boardOf(id, instance.kind), label: "Board" }}
        title="Export as PDF"
        meta="A condensed, print-ready record to hand to an auditor or partner. Choose Save as PDF in the print window. It shows what you may see on the board, as of now."
        actions={
          <button type="button" className="button" onClick={() => window.print()}>
            <Icon name="clipboard" size={18} />
            Export as PDF
          </button>
        }
      />

      <article className="paper" aria-label={`${words(word)} record for ${instance.employeeName}`}>
        <header className="paper-head">
          <div>
            <p className="paper-kicker">
              {org.name} · {words(word)} handoff record
            </p>
            <h2 className="paper-title">{instance.employeeName}</h2>
          </div>
          <p className="paper-stamp">
            <strong>{status}</strong>
            <br />
            As of {whenText(at)}
          </p>
        </header>

        <dl className="paper-facts">
          {facts(instance, employee).map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <p className="paper-line">
          <strong>
            {instance.finished} of {plural(instance.total, "task")} finished
          </strong>
          {outstanding.length > 0 ? `, ${outstanding.length} still to do` : ""}
          {instance.overdue > 0 ? `, ${instance.overdue} late` : ""}
          {blocked > 0 ? `, ${blocked} blocked` : ""}
          {instance.flagged > 0 ? `, ${plural(instance.flagged, "task")} held by somebody who has left` : ""}.
        </p>
        {instance.cancelled ? (
          <p className="paper-line">
            Cancelled by {nameOf(instance.cancelled.by)}: {instance.cancelled.reason}
          </p>
        ) : null}

        {outstanding.length > 0 ? (
          <>
            <h3 className="paper-heading">Still to do ({outstanding.length})</h3>
            <table className="paper-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Department</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {outstanding.map((entry) => (
                  <Outstanding key={entry.card.key} entry={entry} nameOf={nameOf} />
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        <h3 className="paper-heading">Done ({finished.length})</h3>
        {finished.length === 0 ? (
          <p className="paper-line">Nothing has been finished yet.</p>
        ) : (
          <table className="paper-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Department</th>
                <th>Done by</th>
                <th>When</th>
                <th>Recorded</th>
              </tr>
            </thead>
            <tbody>
              {finished.map((entry) => (
                <Finished key={entry.card.key} entry={entry} nameOf={nameOf} />
              ))}
            </tbody>
          </table>
        )}

        <Assurance board={board} audit={audit} />
      </article>
    </section>
  );
}

function facts(instance: BoardResponse["instance"], employee: EmployeeSummary | null): [string, string][] {
  const rows: [string, string][] = [
    ["Type", words(kindWord(instance))],
    [anchorLabel(instance), dayText(instance.anchorDate, true)],
    ["Site", instance.siteName],
  ];
  if (employee) {
    if (employee.department) rows.push(["Department", employee.department]);
    if (employee.employeeType)
      rows.push(["Role", `${words(employee.employeeType)}, ${words(employee.employmentStatus)}`]);
    if (employee.managerName) rows.push(["Manager", employee.managerName]);
    if (employee.endDate) rows.push(["Contract ends", dayText(employee.endDate, true)]);
    rows.push(["Currently", EMPLOYEE_STATE_LABEL[employee.state]]);
  }
  rows.push(["Template", instance.templateName]);
  rows.push(["Started in utuwrk", whenText(instance.createdAt)]);
  if (instance.hipaa) rows.push(["Compliance", "HIPAA site: compliance tasks have a reviewer"]);
  return rows;
}

function Outstanding({ entry, nameOf }: { entry: BoardCard; nameOf: (id: string | null) => string }) {
  const { card } = entry;
  const late = SLA_LABEL[entry.sla];
  return (
    <tr>
      <td>
        {card.title}
        {card.full && card.blockedReason ? (
          <span className="paper-note">Blocked: {card.blockedReason}</span>
        ) : null}
        {card.full && card.waitingOn.length > 0 ? (
          <span className="paper-note">Waiting on {listText(card.waitingOn)}</span>
        ) : null}
      </td>
      <td>{card.department}</td>
      <td>{card.full ? nameOf(card.assigneeId) : "—"}</td>
      <td>
        {STATE_LABEL[card.state]}
        {late ? ` · ${late}` : ""}
        {entry.flagged ? " · hand over" : ""}
      </td>
      <td>{card.full ? dueText(card).replace(/^Due (by )?/, "") : "—"}</td>
    </tr>
  );
}

function Finished({ entry, nameOf }: { entry: BoardCard; nameOf: (id: string | null) => string }) {
  const { card } = entry;
  const notes: string[] = [];
  if (card.state === "skipped")
    notes.push(`Skipped${card.full && card.skipReason ? `: ${card.skipReason}` : ""}`);
  if (card.full) {
    for (const signoff of card.signoffs)
      if (signoff.by)
        notes.push(
          `${signoff.department} sign-off: ${nameOf(signoff.by)}${signoff.at ? `, ${whenText(signoff.at)}` : ""}`,
        );
    notes.push(...recorded(card));
    if (card.access) notes.push(`Access asked for ${whenText(card.access.requestedAt)}`);
    if (card.compliance) notes.push("Compliance task");
  }
  return (
    <tr>
      <td>{card.title}</td>
      <td>{card.department}</td>
      <td>{card.full && card.finishedBy ? nameOf(card.finishedBy) : "—"}</td>
      <td>{card.full && card.finishedAt ? whenText(card.finishedAt) : "—"}</td>
      <td>
        {notes.length
          ? notes.map((note) => (
              <span key={note} className="paper-note paper-note-plain">
                {note}
              </span>
            ))
          : "—"}
      </td>
    </tr>
  );
}

/** What the log says about the record's own trustworthiness: only for whoever may read it. */
function Assurance({ board, audit }: { board: BoardResponse; audit: AuditResponse | null }) {
  const median = audit?.handoffs.medianMs ?? null;
  return (
    <footer className="paper-foot">
      {audit ? (
        <p>
          Audit log: {plural(audit.entries.length, "entry", "entries")},{" "}
          <strong>{audit.intact ? "chain verified intact" : "chain FAILED its check"}</strong> when this was
          made.
          {median !== null
            ? ` Median handoff ${durationText(median)} from a task opening to its owner picking it up.`
            : ""}
        </p>
      ) : (
        <p>
          {board.canReadAudit
            ? "The audit log could not be read when this was made."
            : "The audit log is held by HR, administrators and compliance."}
        </p>
      )}
      <p>
        Private values (identity documents, reference numbers) are never shown here or written to the log.
        Made with utuwrk; the audit log can only be added to.
      </p>
    </footer>
  );
}
